import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Shell from "@/components/Shell";
import CommandCenter from "@/pages/CommandCenter";
import Wallets from "@/pages/Wallets";
import Transactions from "@/pages/Transactions";
import Alerts from "@/pages/Alerts";
import StackOverlay from "@/pages/StackOverlay";
import AgentDetail from "@/pages/AgentDetail";
import GraphWorkspace from "@/pages/GraphWorkspace";
import GlobalMap from "@/pages/GlobalMap";
import StreetView from "@/pages/StreetView";
import Models from "@/pages/Models";
import OnChain from "@/pages/OnChain";
import ApiKeys from "@/pages/ApiKeys";
import UserManagement from "@/pages/UserManagement";
import Settings from "@/pages/Settings";

function FullscreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-nexus-bg text-nexus-cyan font-mono text-xs tracking-[0.3em] uppercase gap-2">
      <Loader2 className="animate-spin" size={16} /> Initializing…
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Handle OAuth callback BEFORE any protected route runs (synchronous check).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <ProtectedRoute>
            <Shell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<CommandCenter />} />
        <Route path="/graph" element={<GraphWorkspace />} />
        <Route path="/map" element={<GlobalMap />} />
        <Route path="/street/:id" element={<StreetView />} />
        <Route path="/onchain" element={<OnChain />} />
        <Route path="/models" element={<Models />} />
        <Route path="/api-keys" element={<Navigate to="/keys" replace />} />
        <Route path="/keys" element={<ApiKeys />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/wallets" element={<Wallets />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/stack" element={<StackOverlay />} />
        <Route path="/agent/:id" element={<AgentDetail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="scanlines min-h-screen font-outfit text-nexus-text">
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
